import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Paramount palette (matches Brief.js exactly — unified in Phase 3)
const OLIVE = '#3d4128';
const CREAM = '#fbf8f3';

const TYPE_META = {
  targeting_mismatch_fix:      { label: 'Fix Targeting',      bg: '#fff7ed', border: '#fdba74', accent: '#9a3412', icon: '\u26a0\ufe0f' },
  cannibalization_consolidate: { label: 'Consolidate Pages',  bg: '#fffbeb', border: '#fcd34d', accent: '#92400e', icon: '\u26a1' },
  ctr_fix:                     { label: 'Fix Click-Through',  bg: '#fefce8', border: '#facc15', accent: '#854d0e', icon: '\ud83c\udfaf' },
  content_gap_create:          { label: 'Create Page',        bg: '#fef2f2', border: '#fca5a5', accent: '#991b1b', icon: '\ud83d\udd0d' },
  striking_distance_push:      { label: 'Push Rankings',      bg: '#eff6ff', border: '#93c5fd', accent: '#1e40af', icon: '\ud83d\ude80' },
  authority_strengthen:        { label: 'Strengthen Authority', bg: '#f0fdf4', border: '#86efac', accent: '#166534', icon: '\ud83c\udf31' },
};

// Same order the engine uses to pick one prescription per keyword (TYPE_PRIORITY).
// Groups render top-to-bottom in this order so the highest-priority work leads.
const TYPE_ORDER = [
  'targeting_mismatch_fix',
  'cannibalization_consolidate',
  'ctr_fix',
  'content_gap_create',
  'striking_distance_push',
  'authority_strengthen',
];

// One plain-language line per group: what this class of problem is, in the user's terms.
const TYPE_SUMMARY = {
  targeting_mismatch_fix:      'Google is ranking a different page than the one built for the keyword. When many cluster here, the cause is usually one internal-linking pattern — fixing that pattern can resolve several at once.',
  cannibalization_consolidate: 'Several of the site\u2019s own pages compete for the same keyword, splitting authority between them.',
  ctr_fix:                     'The page ranks well but earns fewer clicks than that position should. This is title and description work, not content work.',
  content_gap_create:          'Real search demand exists, but no page on the site is built to target it.',
  striking_distance_push:      'Already ranking with traffic and close to the top. A focused push costs far less than ranking new content.',
  authority_strengthen:        'The right page is targeted correctly but lacks the authority to rank higher. An internal-linking and time play.',
};

function formatVol(v) {
  if (v === null || v === undefined) return '\u2014';
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
  return String(v);
}

// ── MoveCard: duplicated verbatim from Brief.js (Option 2). ───────────────────
// Intentionally identical so the two tabs are visually and behaviourally the
// same. Both copies get extracted to one shared component in Phase 3.
function MoveCard({ move, onStatusChange }) {
  const [busy, setBusy] = useState(false);
  const meta = TYPE_META[move.prescription_type] || { label: move.prescription_type, bg: '#f8fafc', border: '#cbd5e1', accent: '#475569', icon: '\ud83d\udca1' };
  const ev = move.evidence || {};

  const setStatus = async (status) => {
    let reason = null;
    if (status === 'dismissed') {
      reason = window.prompt('Why dismiss this? (optional \u2014 helps improve future recommendations)');
      if (reason === null) return; // user cancelled the prompt
    }
    setBusy(true);
    try {
      const updated = await api.setPrescriptionStatus(move.id, status, reason || undefined);
      onStatusChange(updated);
    } catch (e) {
      alert('Could not update: ' + (e.response && e.response.data && e.response.data.message ? e.response.data.message : e.message));
    } finally {
      setBusy(false);
    }
  };

  const accepted = move.status === 'accepted';

  return (
    <div style={{ background: meta.bg, border: '1px solid ' + meta.border, borderLeft: '4px solid ' + meta.accent, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16 }}>{meta.icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: meta.accent, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '2px 8px', background: 'rgba(255,255,255,0.6)', borderRadius: 4 }}>
          {meta.label}
        </span>
        {typeof ev.opportunity_score === 'number' && (
          <span style={{ fontSize: 11, fontWeight: 700, color: meta.accent, padding: '2px 8px', background: 'rgba(255,255,255,0.6)', borderRadius: 4 }}>
            Opportunity {ev.opportunity_score}
          </span>
        )}
        {accepted && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', padding: '2px 8px', background: '#dcfce7', borderRadius: 4 }}>
            Accepted
          </span>
        )}
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>{move.title}</div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, marginBottom: 8 }}>{move.diagnosis}</div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, marginBottom: 10 }}>
        <span style={{ fontWeight: 700, color: meta.accent }}>What to do: </span>{move.action}
      </div>
      {Array.isArray(ev.competing_urls) && ev.competing_urls.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.55)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: meta.accent, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 5 }}>
            Ranking URLs ({ev.competing_urls.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {ev.competing_urls.map(u => (
              <a key={u} href={u} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#374151', wordBreak: 'break-all' }}>{u}</a>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        {move.keyword && <span><b>Keyword:</b> {move.keyword}</span>}
        {ev.volume !== null && ev.volume !== undefined && <span><b>Volume:</b> {formatVol(ev.volume)}/mo</span>}
        {ev.cpc !== null && ev.cpc !== undefined && <span><b>CPC:</b> ${Number(ev.cpc).toFixed(2)}</span>}
        {ev.position !== null && ev.position !== undefined && <span><b>Position:</b> {Number(ev.position).toFixed(1)}</span>}
        {move.target_url && (
          <span>
            <b>Page: </b>
            <a href={move.target_url} target="_blank" rel="noopener noreferrer" style={{ color: OLIVE, wordBreak: 'break-all' }}>{move.target_url}</a>
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {!accepted && (
          <button
            disabled={busy}
            onClick={() => setStatus('accepted')}
            style={{ padding: '7px 16px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}
          >
            Accept
          </button>
        )}
        <button
          disabled={busy}
          onClick={() => setStatus('dismissed')}
          style={{ padding: '7px 16px', background: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ── Group header: type label, count, one-line meaning, collapse toggle ────────
function GroupHeader({ type, count, open, onToggle }) {
  const meta = TYPE_META[type] || { label: type, accent: '#475569', icon: '\ud83d\udca1' };
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none', padding: '4px 0',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      <span style={{ fontSize: 13, color: meta.accent, marginTop: 2, width: 14, flexShrink: 0 }}>{open ? '\u25be' : '\u25b8'}</span>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{meta.icon}</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: OLIVE }}>{meta.label}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: meta.accent, background: meta.bg, padding: '1px 8px', borderRadius: 10 }}>{count}</span>
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: '#64748b', lineHeight: 1.5, marginTop: 3 }}>{TYPE_SUMMARY[type] || ''}</span>
      </span>
    </button>
  );
}

export default function Opportunities({ websiteId }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all | proposed | accepted
  const [openGroups, setOpenGroups] = useState({});        // type -> bool

  const load = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getPrescriptions(websiteId);
      setPrescriptions(data.prescriptions || []);
      setDomain(data.domain || '');
      // Open the highest-priority group by default; collapse the rest.
      const present = TYPE_ORDER.filter(t => (data.prescriptions || []).some(p => p.prescription_type === t));
      setOpenGroups(present.reduce((acc, t, i) => { acc[t] = i === 0; return acc; }, {}));
    } catch (e) {
      setError(e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = (updated) => {
    setPrescriptions(prev => {
      if (updated.status === 'dismissed') {
        return prev.filter(p => p.id !== updated.id);
      }
      return prev.map(p => (p.id === updated.id ? updated : p));
    });
  };

  const toggleGroup = (type) => setOpenGroups(prev => ({ ...prev, [type]: !prev[type] }));

  if (!websiteId) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Select a website to see its opportunities.</div>;
  }
  if (loading && prescriptions.length === 0) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Loading opportunities{'\u2026'}</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ color: '#991b1b', fontSize: 14, marginBottom: 12 }}>Could not load opportunities: {error}</div>
        <button onClick={load} style={{ padding: '8px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  // Empty: no prescriptions written yet. Direction, not decoration — point to the Brief tab.
  if (prescriptions.length === 0) {
    return (
      <div style={{ padding: '40px 24px', maxWidth: 640 }}>
        <h2 style={{ margin: '0 0 8px', color: OLIVE, fontSize: 22 }}>Opportunities</h2>
        <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
          No opportunities yet for this site. Open the <b>Brief</b> tab and generate a brief {'\u2014'} it analyses keywords,
          coverage, and demand, then writes the prescriptions that appear here as a work queue.
        </div>
      </div>
    );
  }

  const filtered = statusFilter === 'all'
    ? prescriptions
    : prescriptions.filter(p => p.status === statusFilter);

  // Group filtered prescriptions by type, preserving TYPE_ORDER.
  const groups = TYPE_ORDER
    .map(type => ({ type, items: filtered.filter(p => p.prescription_type === type) }))
    .filter(g => g.items.length > 0);

  // Concentration strip: counts per type across the full (unfiltered) set.
  const counts = TYPE_ORDER
    .map(type => ({ type, n: prescriptions.filter(p => p.prescription_type === type).length }))
    .filter(c => c.n > 0);

  const acceptedCount = prescriptions.filter(p => p.status === 'accepted').length;

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0, color: OLIVE, fontSize: 22 }}>Opportunities</h2>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {domain} {'\u00b7'} {prescriptions.length} open {prescriptions.length === 1 ? 'move' : 'moves'}
          {acceptedCount > 0 ? ' \u00b7 ' + acceptedCount + ' accepted' : ''}
        </div>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
        Every open move for this site, grouped by the kind of work it takes. Highest-priority group first.
      </div>

      {/* Concentration strip — where the work is bunched */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {counts.map(c => {
          const meta = TYPE_META[c.type];
          return (
            <span key={c.type} style={{ fontSize: 12, fontWeight: 600, color: meta.accent, background: meta.bg, border: '1px solid ' + meta.border, padding: '4px 10px', borderRadius: 6 }}>
              {meta.label}: {c.n}
            </span>
          );
        })}
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {[['all', 'All'], ['proposed', 'Proposed'], ['accepted', 'Accepted']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setStatusFilter(val)}
            style={{
              padding: '5px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
              border: '1px solid ' + (statusFilter === val ? OLIVE : '#cbd5e1'),
              background: statusFilter === val ? OLIVE : 'transparent',
              color: statusFilter === val ? CREAM : '#475569',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <div style={{ fontSize: 13, color: '#64748b', padding: '12px 0' }}>
          No {statusFilter} moves. Switch the filter above to see the rest.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {groups.map(g => {
            const open = !!openGroups[g.type];
            return (
              <div key={g.type} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
                <GroupHeader type={g.type} count={g.items.length} open={open} onToggle={() => toggleGroup(g.type)} />
                {open && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                    {g.items.map(move => (
                      <MoveCard key={move.id} move={move} onStatusChange={handleStatusChange} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
